<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';

import ButtonWithLoader from '@/components/buttons/ButtonWithLoader.vue';
import XMarkSolid from '@/components/svg/XMarkSolid.vue';
import type { LocaleSchema } from '@/locales/index.ts';

const isOpen = defineModel<boolean>('open', { default: false });

const { t } = useI18n<{message: LocaleSchema}>({
  useScope: 'global',
});
const dialogElement = useTemplateRef<HTMLDialogElement>('dialog');
const bodySentinelTop = useTemplateRef<HTMLAnchorElement>('bodySentinelTop');
const bodySentinelBottom = useTemplateRef<HTMLAnchorElement>('bodySentinelBottom');

defineExpose({
  dialogElement
});

const props = defineProps<{
  hideCloseButton?: boolean;
  hideCancelButton?: boolean;
  hideConfirmButton?: boolean;
  showFooter?: boolean;
  confirmButtonLabel?: string;
  cancelButtonLabel?: string;
  confirmButtonId?: string;
  pending?: boolean;
  nonModal?: boolean;
}>();

const emit = defineEmits<{
  'confirm': [];
}>();

const bodyIsScrolledToBottom = ref(false);
const bodyIsScrolledToTop = ref(false);

const cancelLabel = computed(() => {
  return props.cancelButtonLabel || t('browsingApp.actions.cancel');
});

const confirmLabel = computed(() => {
  return props.confirmButtonLabel || t('browsingApp.actions.confirm');
});

function onConfirm() {
  emit('confirm');
}

function onDialogClose() {
  isOpen.value = false;
}

function onDialogMouseDown(event: MouseEvent) {
  if (event.target === dialogElement.value) {
    isOpen.value = false;
  }
}

function onCloseButtonClick() {
  onDialogClose();
}

onMounted(() => {
  if (!dialogElement.value) return;
  
  const observerTop = new IntersectionObserver(([entry]) => {
    bodyIsScrolledToTop.value = entry.isIntersecting;
  }, { root: dialogElement.value, threshold: 0 });
  
  const observerBottom = new IntersectionObserver(([entry]) => {
    bodyIsScrolledToBottom.value = entry.isIntersecting;
  }, { root: dialogElement.value, threshold: 0 });
  
  if (bodySentinelTop.value) {
    observerTop.observe(bodySentinelTop.value);
  }
  
  if (bodySentinelBottom.value) {
    observerBottom.observe(bodySentinelBottom.value);
  }
});

watch(isOpen, (newValue) => {
  if(!dialogElement.value) return;
  
  if (newValue) {
    if(props.nonModal) {
      dialogElement.value.show();
      return;
    }
    
    dialogElement.value.showModal();
  } else {
    dialogElement.value.close();
  }
}, { deep: true });

</script>

<template>
  <dialog ref="dialog" 
          class="dialogModal"
          @close="onDialogClose"
          @mousedown="onDialogMouseDown">
    <button v-if="!hideCloseButton"
            class="dialogModal-closeButton"
            aria-label="Close dialog"
            @click="onCloseButtonClick">
      <XMarkSolid />
    </button>
    <slot name="container">
      <div class="dialogModal-content">
        <slot name="header"
              v-bind="{ bodyIsScrolledToTop }">
          <header class="dialogModal-header"
                  :data-body-scrolled-to-top="bodyIsScrolledToTop">
            <slot name="headerContent"></slot>
          </header>
        </slot>
        <div class="dialogModal-body">
          <a ref="bodySentinelTop"
              class="sentinel" />
          <slot></slot>
          <a ref="bodySentinelBottom"
              class="sentinel" />
        </div>
        <slot name="footer">
          <footer v-if="showFooter"
                  class="dialogModal-footer"
                  :data-body-scrolled-to-bottom="bodyIsScrolledToBottom">
            <slot name="footerContent">
              <div class="dialogModal-actions">
                <slot name="footerActions">
                  <button v-if="!hideCancelButton"
                          class="btn btn_secondary"
                          :disabled="pending"
                          @click="onDialogClose">
                    {{ cancelLabel }}
                  </button>
                  <ButtonWithLoader v-if="!hideConfirmButton"
                                    :id="confirmButtonId"
                                    :label="confirmLabel"
                                    :pending="pending"
                                    @click="onConfirm" />
                </slot>
              </div>
            </slot>
          </footer>
        </slot>
      </div>
    </slot>
  </dialog>
</template>

<style lang="scss">

</style>
